import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Package, Crown, ShoppingBag } from "lucide-react";

const STATUS_COLOR = {
  paid: "text-[#4CAF50]",
  initiated: "text-[#8D9688]",
  expired: "text-[#D32F2F]",
  failed: "text-[#D32F2F]",
};

const FULFILLMENT_LABEL = {
  submitted: "Submitted to Printful",
  manual_required: "Manual review",
  failed: "Fulfillment retry needed",
};

export default function OrderHistory() {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get("/orders")
      .then((r) => setOrders(r.data))
      .finally(() => setLoading(false));
  }, [user]);

  if (authLoading) return null;

  if (!user) {
    return (
      <div className="min-h-[70vh] max-w-2xl mx-auto px-4 md:px-8 py-20 text-center" data-testid="orders-no-auth">
        <Package className="w-12 h-12 text-[#DD5422] mx-auto mb-4" />
        <h1 className="font-anton text-4xl uppercase tracking-tight">Sign In to See Your Orders</h1>
        <p className="text-[#8D9688] mt-3">Track your purchases and Printful fulfillment status.</p>
        <Link to="/"><Button className="mt-6 bg-[#DD5422] text-[#0A0E0B] hover:bg-[#FF6B35] rounded-none font-anton uppercase tracking-widest border-2 border-[#DD5422] h-12 px-8">Go Home</Button></Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-20" data-testid="orders-page">
      <div className="mb-12">
        <div className="font-anton uppercase tracking-[0.3em] text-[#DD5422] text-sm mb-2">/ Your Stuff</div>
        <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tight">Order History</h1>
      </div>

      {loading ? (
        <div className="text-[#8D9688] uppercase tracking-widest" data-testid="orders-loading">Loadin' up...</div>
      ) : orders.length === 0 ? (
        <div className="bg-[#151A14] border-2 border-[#2C332A] p-10 text-center" data-testid="orders-empty">
          <ShoppingBag className="w-12 h-12 text-[#DD5422] mx-auto mb-3" />
          <div className="font-anton text-2xl uppercase">No orders yet, partner</div>
          <p className="text-[#8D9688] mt-2">Once you snag some merch, you'll see it here.</p>
          <Link to="/shop"><Button className="mt-6 bg-[#DD5422] text-[#0A0E0B] hover:bg-[#FF6B35] rounded-none font-anton uppercase tracking-widest border-2 border-[#DD5422] h-12 px-8">Shop the Drop</Button></Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o, i) => (
            <div key={o.session_id} data-testid={`order-${o.session_id}`}
              className="bg-[#151A14] border-2 border-[#2C332A] hover:border-[#DD5422] transition-colors p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
              <div className="md:w-12 flex-shrink-0">
                {o.type === "subscription" ? (
                  <Crown className="w-8 h-8 text-[#DD5422]" />
                ) : (
                  <Package className="w-8 h-8 text-[#DD5422]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-anton text-lg md:text-xl uppercase tracking-tight truncate">{o.product_name || "Order"}</div>
                <div className="text-xs text-[#8D9688] uppercase tracking-widest mt-1">
                  {o.variant_label && `${o.variant_label} · `}Qty {o.quantity} · {new Date(o.created_at).toLocaleDateString()}
                </div>
                {o.fulfillment_status && (
                  <div className="text-xs text-[#DD5422] uppercase tracking-widest mt-1">
                    {FULFILLMENT_LABEL[o.fulfillment_status] || o.fulfillment_status}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="font-anton text-xl text-[#E5E5E5]">${o.amount.toFixed(2)}</div>
                <div className={`text-xs uppercase tracking-widest mt-1 ${STATUS_COLOR[o.payment_status] || "text-[#8D9688]"}`}>
                  {o.payment_status || "pending"}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
